/**
* Web.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { objDefineAccessors, getWindow, getLocation, safeGetCookieMgr, getCookieValue, getNavigator, isArray } from "@microsoft/1ds-core-js";
var REGEX_VERSION = "([\\d,.]+)";
var UNKNOWN = "Unknown";
var EDGE_CHROMIUM = "Edg/";
var USER_AGENTS = [
    { ua: "OPR/", b: "Opera" /* OPERA */ },
    { ua: "PhantomJS" /* PHANTOMJS */, b: "PhantomJS" /* PHANTOMJS */ },
    { ua: "Edge" /* EDGE */, b: "Edge" /* EDGE */ },
    { ua: EDGE_CHROMIUM, b: "Edge" /* EDGE */ },
    { ua: "Electron" /* ELECTRON */, b: "Electron" /* ELECTRON */ },
    { ua: "Chrome" /* CHROME */, b: "Chrome" /* CHROME */ },
    { ua: "Trident", b: "MSIE" /* MSIE */ },
    { ua: "MSIE ", b: "MSIE" /* MSIE */ },
    { ua: "Firefox" /* FIREFOX */, b: "Firefox" /* FIREFOX */ },
    { ua: "Safari" /* SAFARI */, b: "Safari" /* SAFARI */ },
    { ua: "SkypeShell" /* SKYPE_SHELL */, b: "SkypeShell" /* SKYPE_SHELL */ } // Check for Skype shell
];
var BRANDS = [
    { br: "Microsoft Edge", b: "Edge" /* EDGE */ },
    { br: "Google Chrome", b: "Chrome" /* CHROME */ },
    { br: "Opera", b: "Opera" /* OPERA */ }
];
function _userAgentContainsString(searchString, userAgent) {
    return userAgent.indexOf(searchString) > -1;
}
function _getBrandVersion(match, brands) {
    for (var lp = 0; lp < brands.length; lp++) {
        if (match == brands[lp].brand) {
            return brands[lp].version;
        }
    }
    return null;
}
function _getBrowserName(userAgent) {
    if (userAgent) {
        for (var lp = 0; lp < USER_AGENTS.length; lp++) {
            var ua = USER_AGENTS[lp].ua;
            if (_userAgentContainsString(ua, userAgent)) {
                return USER_AGENTS[lp].b;
            }
        }
    }
    return UNKNOWN;
}
function _getBrowserVersion(userAgent, browserName) {
    if (browserName === "MSIE" /* MSIE */) {
        return _getIeVersion(userAgent);
    }
    return _getOtherVersion(browserName, userAgent);
}
function _getIeVersion(userAgent) {
    var classicIeVersionMatches = userAgent.match(new RegExp("MSIE" /* MSIE */ + " " + REGEX_VERSION));
    if (classicIeVersionMatches) {
        return classicIeVersionMatches[1];
    }
    var ieVersionMatches = userAgent.match(new RegExp("rv:" + REGEX_VERSION));
    if (ieVersionMatches) {
        return ieVersionMatches[1];
    }
}
function _getOtherVersion(browserString, userAgent) {
    if (browserString === "Safari" /* SAFARI */) {
        browserString = "Version";
    }
    else if (browserString === "Edge" /* EDGE */) {
        if (_userAgentContainsString(EDGE_CHROMIUM, userAgent)) {
            browserString = "Edg";
        }
    }
    var matches = userAgent.match(new RegExp(browserString + "/" + REGEX_VERSION));
    if (matches) {
        return matches[1];
    }
    if (browserString === "Opera" /* OPERA */) {
        matches = userAgent.match(new RegExp("OPR/" + REGEX_VERSION));
        if (matches) {
            return matches[1];
        }
    }
    return UNKNOWN;
}
/**
* Get Screen resolution
* @returns {ScreenResolution} - Screen resolution
*/
function _getScreenResolution() {
    var screenRes = { h: 0, w: 0 };
    var win = getWindow();
    if (win && win.screen) {
        screenRes.h = screen.height;
        screenRes.w = screen.width;
    }
    return screenRes;
}
function _getUserConsent() {
    return this.getUserConsent();
}
var Web = /** @class */ (function () {
    function Web(propertiesConfig, core) {
        var _cookieMgr = safeGetCookieMgr(core);
        var _propertiesConfig = propertiesConfig || {};
        dynamicProto(Web, this, function (_self) {
            // Add the domain
            var windowLocation = getLocation();
            if (windowLocation) {
                var domain = windowLocation.hostname;
                if (domain) {
                    _self.domain = windowLocation.protocol === "file:" ? "local" : domain;
                }
            }
            if (_propertiesConfig.populateBrowserInfo) {
                var userAgent = _propertiesConfig.userAgent;
                var userAgentBrands = (_propertiesConfig.userAgentData || {}).brands;
                var theNav = getNavigator();
                if (theNav) {
                    userAgent = userAgent || theNav.userAgent || "";
                    userAgentBrands = userAgentBrands || (theNav.userAgentData || {}).brands;
                }
                _parseUserAgent(userAgent, userAgentBrands);
                var screenRes = _getScreenResolution();
                _self.screenRes = screenRes.w + "X" + screenRes.h;
            }
            _self.getUserConsent = function () {
                return _propertiesConfig.userConsented || (getCookieValue(_cookieMgr, _propertiesConfig.userConsentCookieName || "MSCC") ? true : false);
            };
            /**
            *
            ** Function to retrieve user consent details.
            * @param callback - Callback function to get user consent details
            * @returns IUserContentDetails stringified object
            */
            _self.getUserConsentDetails = function () {
                try {
                    var callback = _propertiesConfig.callback;
                    if (callback && callback.userConsentDetails) {
                        var result = callback.userConsentDetails();
                        if (result) {
                            return JSON.stringify({
                                Required: result.Required || false,
                                Analytics: result.Analytics || false,
                                SocialMedia: result.SocialMedia || false,
                                Advertising: result.Advertising || false
                            });
                        }
                    }
                }
                catch (e) {
                    // Unexpected - Just making sure we don't crash
                }
                return null;
            };
            function _parseUserAgent(userAgent, userAgentBrands) {
                if (isArray(userAgentBrands)) {
                    try {
                        // Go through the ordered list of "known" brands and use the first matching value
                        for (var lp = 0; lp < BRANDS.length; lp++) {
                            var version = _getBrandVersion(BRANDS[lp].br, userAgentBrands);
                            if (version) {
                                _self.browser = BRANDS[lp].b;
                                _self.browserVer = version;
                                return;
                            }
                        }
                    }
                    catch (e) {
                        // Unexpected - Just making sure we don't crash
                    }
                }
                if (userAgent) {
                    var browserName = _getBrowserName(userAgent);
                    _self.browser = browserName;
                    _self.browserVer = _getBrowserVersion(userAgent, browserName);
                }
            }
            // Remap this get userContext for this instance
            objDefineAccessors(_self, "userConsent", _self.getUserConsent);
        });
    }
    /**
    * Describes whether the user has given consent for Cookies.
    * This is configurable by users to define which cookie to look for marking this flag as true or false.
    */
    Web.prototype.getUserConsent = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
    * Function to retrieve user consent details.
    * @param callback - Callback function to get user consent details
    * @returns IUserContentDetails stringified object
    */
    Web.prototype.getUserConsentDetails = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Static constructor, attempt to create accessors
     */
    Web._staticInit = (function () {
        // Dynamically create get/set property accessors
        objDefineAccessors(Web.prototype, "userConsent", _getUserConsent);
    })();
    return Web;
}());
export { Web };
//# sourceMappingURL=Web.js.map